import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Sun, Plus, X, CalendarClock, AlertTriangle, ListTodo, Building2, Users, Pencil, ChevronDown } from "lucide-react";
import { useTaskEditor } from "@/context/TaskEditorContext";

const COLLAPSE_KEY = "meintag_collapsed";
const loadCollapsed = () => {
  try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY)) || {}; } catch { return {}; }
};

const priorityBadge = (priority) => {
  switch (priority) {
    case "Hoch":
      return "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300";
    case "Mittel":
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300";
    default:
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300";
  }
};

export default function MeinTag({ embedded = false }) {
  const toast = useToast();
  const { openEdit } = useTaskEditor();
  const [data, setData] = useState({ date: null, tasks: [], suggestions: [], total: 0, done: 0 });
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(loadCollapsed);

  const toggleCollapse = (key) => setCollapsed((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  const fetchMyDay = useCallback(async () => {
    try {
      const res = await api.get("/my-day");
      const d = res.data || {};
      setData({
        date: d.date,
        tasks: Array.isArray(d.tasks) ? d.tasks : [],
        suggestions: Array.isArray(d.suggestions) ? d.suggestions : [],
        total: d.total || 0,
        done: d.done || 0,
      });
    } catch (e) {
      toast.error("Fehler beim Laden von Mein Tag");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchMyDay();
  }, [fetchMyDay]);

  useEffect(() => {
    const onChange = () => fetchMyDay();
    window.addEventListener("tasks-changed", onChange);
    return () => window.removeEventListener("tasks-changed", onChange);
  }, [fetchMyDay]);

  const dateLabel = data.date
    ? new Date(`${data.date}T00:00:00`).toLocaleDateString("de-DE", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  const toggleDone = async (task) => {
    const newStatus = task.status === "Erledigt" ? "Offen" : "Erledigt";
    try {
      await api.put(`/tasks/${task.id}`, { status: newStatus });
      fetchMyDay();
      window.dispatchEvent(new Event("tasks-changed"));
    } catch (e) {
      toast.error("Status konnte nicht geändert werden");
    }
  };

  const addToDay = async (taskId) => {
    try {
      await api.post("/my-day", { task_id: taskId });
      fetchMyDay();
    } catch (e) {
      toast.error("Konnte nicht hinzugefügt werden");
    }
  };

  const removeFromDay = async (taskId) => {
    try {
      await api.delete(`/my-day/${taskId}`);
      fetchMyDay();
    } catch (e) {
      toast.error("Konnte nicht entfernt werden");
    }
  };

  const progress = data.total > 0 ? Math.round((data.done / data.total) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mein-tag-page">
      {/* Header */}
      {embedded ? (
        <p className="text-muted-foreground capitalize -mt-2" data-testid="mein-tag-date">{dateLabel}</p>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Sun className="w-7 h-7 text-primary" />
              Mein Tag
            </h1>
            <p className="text-muted-foreground capitalize" data-testid="mein-tag-date">{dateLabel}</p>
          </div>
        </div>
      )}

      {/* Progress */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" data-testid="mein-tag-progress-label">
              {data.done} von {data.total} erledigt
            </span>
            <span className="text-sm text-muted-foreground">{progress}%</span>
          </div>
          <Progress value={progress} data-testid="mein-tag-progress-bar" />
        </CardContent>
      </Card>

      {/* Today's tasks */}
      <Card>
        <CardHeader>
          <button type="button" onClick={() => toggleCollapse("today")} className="w-full flex items-center justify-between gap-2 text-left" data-testid="mein-tag-toggle-today" aria-expanded={!collapsed.today}>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ListTodo className="w-5 h-5" />
              Heute zu erledigen
              <Badge variant="secondary" className="ml-1">{data.tasks.length}</Badge>
            </CardTitle>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${collapsed.today ? "-rotate-90" : ""}`} />
          </button>
        </CardHeader>
        {!collapsed.today && (
        <CardContent className="space-y-2">
          {data.tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="mein-tag-empty">
              <Sun className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Noch keine Aufgaben für heute geplant.</p>
              <p className="text-sm">Fügen Sie unten aus den Vorschlägen hinzu.</p>
            </div>
          ) : (
            data.tasks.map((task) => (
              <div
                key={task.id}
                className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                data-testid={`mein-tag-task-${task.id}`}
              >
                <Checkbox
                  checked={task.status === "Erledigt"}
                  onCheckedChange={() => toggleDone(task)}
                  className="mt-1"
                  data-testid={`mein-tag-task-check-${task.id}`}
                />
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-medium break-words ${
                      task.status === "Erledigt" ? "line-through text-muted-foreground" : ""
                    }`}
                  >
                    {task.title}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {task.is_team && (
                      <Badge variant="secondary" className="bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300 flex items-center gap-1" data-testid={`mein-tag-team-badge-${task.id}`}>
                        <Users className="w-3 h-3" /> Team
                      </Badge>
                    )}
                    <Badge variant="secondary" className={priorityBadge(task.priority)}>
                      {task.priority}
                    </Badge>
                    {task.customer_name && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {task.customer_name}
                      </span>
                    )}
                    {task.due_date && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" />
                        {new Date(`${task.due_date.substring(0, 10)}T00:00:00`).toLocaleDateString("de-DE")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-primary"
                    onClick={() => openEdit(task)}
                    data-testid={`mein-tag-edit-${task.id}`}
                    title="Bearbeiten"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`text-muted-foreground hover:text-destructive ${task.is_team ? "invisible pointer-events-none" : ""}`}
                    onClick={() => removeFromDay(task.id)}
                    data-testid={`mein-tag-remove-${task.id}`}
                    title="Aus Mein Tag entfernen"
                    disabled={task.is_team}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
        )}
      </Card>

      {/* Suggestions */}
      <Card>
        <CardHeader>
          <button type="button" onClick={() => toggleCollapse("suggestions")} className="w-full flex items-center justify-between gap-2 text-left" data-testid="mein-tag-toggle-suggestions" aria-expanded={!collapsed.suggestions}>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Plus className="w-5 h-5" />
              Vorschläge für heute
              <Badge variant="secondary" className="ml-1">{data.suggestions.length}</Badge>
            </CardTitle>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${collapsed.suggestions ? "-rotate-90" : ""}`} />
          </button>
        </CardHeader>
        {!collapsed.suggestions && (
        <CardContent className="space-y-2">
          {data.suggestions.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-sm" data-testid="mein-tag-suggestions-empty">
              Keine offenen Aufgaben. Erstellen Sie neue unter{" "}
              <Link to="/tasks" className="text-primary underline">Aufgaben</Link>.
            </div>
          ) : (
            data.suggestions.map((task) => (
              <div
                key={task.id}
                className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30"
                data-testid={`mein-tag-suggestion-${task.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium break-words">{task.title}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {task.overdue && (
                      <Badge variant="secondary" className="bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Überfällig
                      </Badge>
                    )}
                    {task.due_today && !task.overdue && (
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                        Heute fällig
                      </Badge>
                    )}
                    <Badge variant="secondary" className={priorityBadge(task.priority)}>
                      {task.priority}
                    </Badge>
                    {task.customer_name && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {task.customer_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-primary"
                    onClick={() => openEdit(task)}
                    data-testid={`mein-tag-suggestion-edit-${task.id}`}
                    title="Bearbeiten"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addToDay(task.id)}
                    data-testid={`mein-tag-add-${task.id}`}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Mein Tag
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
        )}
      </Card>
    </div>
  );
}
